import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/react';
import {CountryCode} from 'libphonenumber-js';
import {
  Portal,
  PortalManager,
  Flag,
  Separator,
  ColorSchemeType,
  DataStatus,
} from 'platform/components';
import {Box, Center, Heading, HStack, Icon, Text, VStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {useTheme} from 'styled-components';
import {Nullish} from 'utility-types';

import {useEffect} from 'react';

import {useGetTagsForEntityQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {TestDriveState, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, getNaturalPersonFullName} from '@dms/shared';

import {parseDate} from 'shared/web';

import {TestDriveCalendarEvent} from './utils/transformTestDriveToCalendarEvent';

interface CustomerDetailsPopupProps {
  event: TestDriveCalendarEvent;
  isVisible: boolean;
  anchorElement: HTMLElement | null;
}

const OFFSET_FROM_CURSOR = 10;

const TEST_DRIVE_STATE_FLAG_MAP: Record<
  TestDriveState,
  {colorScheme: ColorSchemeType; label: string}
> = {
  planned: {
    colorScheme: 'yellow',
    label: i18n.t('entity.testDrive.labels.planned'),
  },
  completed: {colorScheme: 'green', label: i18n.t('entity.testDrive.labels.completed')},
  cancelled: {colorScheme: 'red', label: i18n.t('entity.testDrive.labels.cancelled')},
} as const;

export function CustomerDetailsPopup(props: CustomerDetailsPopupProps) {
  const theme = useTheme();
  const formatDateTime = useDateTimeFormatter();

  const customerId = props.event.customerId;

  const {refs, floatingStyles} = useFloating({
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [
      offset(OFFSET_FROM_CURSOR),
      flip({
        fallbackPlacements: ['right-end', 'left-start', 'left-end', 'top', 'bottom'],
        padding: 8,
      }),
      shift({padding: 8, limiter: undefined}),
    ],
    whileElementsMounted: autoUpdate,
  });
  const {setFloating} = refs;

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {customerId},
    {
      skip: !customerId || !props.isVisible,
    }
  );

  const {
    data: vehicleData,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId: props.event.vehicleId},
    {
      skip: !props.event.vehicleId || !props.isVisible,
    }
  );

  const {data: entityTags, isLoading: isEntityTagsLoading} = useGetTagsForEntityQuery({
    recordId: customerId,
    resourceId: EntityResourceIds.customer,
  });

  useEffect(() => {
    refs.setReference(props.anchorElement);
  }, [props.anchorElement, refs]);

  if (!props.isVisible || !customer) {
    return null;
  }

  const testDriveEvent = props.event;
  const isCompany = customer?.discriminator === 'BUSINESS';
  const customerIcon = isCompany ? 'communication/business' : 'social/person';

  const testDriveFlag = TEST_DRIVE_STATE_FLAG_MAP[props.event.testDriveData?.state];

  const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];
  const customerName =
    customer?.discriminator === 'NATURAL_PERSON'
      ? getNaturalPersonFullName(customerInfo)
      : customer.businessInfo?.businessInfo?.tradeName;

  const phoneNumberData = customerInfo?.phoneNumbers?.[0];
  const personPhoneNumber =
    phoneNumberData &&
    formatPhoneNumber(
      `${phoneNumberData?.prefix}${phoneNumberData?.number}`,
      phoneNumberData?.countryCode as CountryCode | Nullish
    );

  const isLoading = isEntityTagsLoading || isVehicleLoading || isCustomerLoading;
  const isError = isCustomerError || isVehicleError;

  return (
    <PortalManager zIndex={theme.zIndices.TOOLTIP}>
      <Portal>
        <div ref={setFloating} style={{...floatingStyles, pointerEvents: 'none'}}>
          <DataStatus isLoading={isLoading} isError={isError}>
            <Box
              backgroundColor="general.white"
              border="1px solid"
              borderColor="palettes.neutral.40.100"
              borderRadius="medium"
              padding={4}
              boxShadow="elevation_4"
              width={100}
              maxWidth={100}
            >
              <VStack spacing={4}>
                <HStack spacing={2}>
                  <Heading size={4}>{vehicleData?.title ?? EMPTY_PLACEHOLDER}</Heading>
                  <Flag colorScheme={testDriveFlag.colorScheme} label={testDriveFlag.label} />
                </HStack>

                <HStack width="100%" justify="space-between">
                  <Box flex={1}>
                    <HStack spacing={4} align="center">
                      <Box
                        width={10}
                        height={10}
                        backgroundColor="palettes.neutral.30.100"
                        borderRadius="small"
                      >
                        <Center height="100%" width="100%">
                          <Icon value="navigation/arrow_upward" size={6} />
                        </Center>
                      </Box>

                      <VStack spacing={0}>
                        <Heading size={5}>
                          {formatDateTime(
                            'dateMedium',
                            parseDate(testDriveEvent.testDriveData.scheduledAt)
                          )}
                        </Heading>
                        <Text size="small" color="secondary">
                          {formatDateTime(
                            'timeShort',
                            parseDate(testDriveEvent.testDriveData.scheduledAt)
                          )}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>

                  <Box flex={1}>
                    <HStack spacing={4} align="center">
                      <Box
                        width={10}
                        height={10}
                        backgroundColor="palettes.neutral.30.100"
                        borderRadius="small"
                      >
                        <Center height="100%" width="100%">
                          <Icon value="navigation/arrow_upward" size={6} />
                        </Center>
                      </Box>

                      <VStack spacing={0}>
                        <Heading size={5}>
                          {formatDateTime(
                            'dateMedium',
                            parseDate(testDriveEvent.testDriveData.returnedAt)
                          )}
                        </Heading>
                        <Text size="small" color="secondary">
                          {formatDateTime(
                            'timeShort',
                            parseDate(testDriveEvent.testDriveData.returnedAt)
                          )}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </HStack>

                <Separator spacing={0} />

                <VStack spacing={4}>
                  <HStack spacing={1} align="center">
                    <Icon value={customerIcon} size={4} />
                    <Heading size={5}>{i18n.t('entity.customer.labels.customer')}</Heading>

                    <HStack spacing={1}>
                      {entityTags?.map((tag) => (
                        <Flag
                          key={tag.tagId}
                          size="small"
                          colorScheme={tag.valueColor}
                          label={tag.valueName}
                        />
                      ))}
                    </HStack>
                  </HStack>

                  <VStack spacing={1}>
                    <HStack width="100%" justify="space-between">
                      <Box flex={1}>
                        <Text size="small" color="secondary">
                          {i18n.t('entity.person.labels.fullName')}
                        </Text>
                      </Box>
                      <Box flex={1}>
                        <Text size="small">{customerName ?? EMPTY_PLACEHOLDER}</Text>
                      </Box>
                    </HStack>

                    <Separator spacing={2} />

                    <HStack width="100%" justify="space-between">
                      <Box flex={1}>
                        <Text size="small" color="secondary">
                          {i18n.t('entity.person.labels.phoneNumber')}
                        </Text>
                      </Box>
                      <Box flex={1}>
                        <Text size="small">{personPhoneNumber ?? EMPTY_PLACEHOLDER}</Text>
                      </Box>
                    </HStack>
                  </VStack>
                </VStack>
              </VStack>
            </Box>
          </DataStatus>
        </div>
      </Portal>
    </PortalManager>
  );
}
